<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as am5 from '@amcharts/amcharts5'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import * as am5map from '@amcharts/amcharts5/map'
import am5geodata_worldLow from '@amcharts/amcharts5-geodata/worldLow'

interface MapPoint {
  latitude: number
  longitude: number
  title: string
}

type HeatmapPoint = [number, number, number]

export interface MapRendererConfig {
  points?: MapPoint[]
  data?: HeatmapPoint[]
}

const props = defineProps<{
  config: MapRendererConfig
  chartType: 'map' | 'heatmap'
}>()

const mapContainer = ref<HTMLDivElement | null>(null)
let am5Root: am5.Root | null = null

const renderBubbleMap = (root: am5.Root, data: MapPoint[]) => {
  const chart = root.container.children.push(
    am5map.MapChart.new(root, {
      panX: 'rotateX',
      panY: 'translateY',
      projection: am5map.geoMercator(),
    }),
  )

  chart.set('zoomControl', am5map.ZoomControl.new(root, {}))

  chart.series.push(
    am5map.MapPolygonSeries.new(root, {
      geoJSON: am5geodata_worldLow,
      exclude: ['AQ'],
      fill: am5.color(0xdddddd),
      stroke: am5.color(0xffffff),
    }),
  )

  const pointSeries = chart.series.push(am5map.MapPointSeries.new(root, {}))
  pointSeries.bullets.push(() =>
    am5.Bullet.new(root, {
      sprite: am5.Circle.new(root, {
        radius: 5,
        fill: am5.color(0x007bff),
        tooltipText: '{title}',
      }),
    }),
  )

  const mapData = data.map((c) => ({
    geometry: { type: 'Point', coordinates: [c.longitude, c.latitude] },
    title: c.title,
  }))
  pointSeries.data.setAll(mapData)
}

// --- Logica per Heatmap ---
const renderHeatmap = (root: am5.Root, data: HeatmapPoint[]) => {
  const chart = root.container.children.push(
    am5map.MapChart.new(root, {
      panX: 'rotateX',
      panY: 'translateY',
      projection: am5map.geoMercator(),
      homeZoomLevel: 1.5,
      homeGeoPoint: { latitude: 20, longitude: 0 },
    }),
  )

  chart.set('zoomControl', am5map.ZoomControl.new(root, {}))

  const polygonSeries = chart.series.push(
    am5map.MapPolygonSeries.new(root, {
      geoJSON: am5geodata_worldLow,
      exclude: ['AQ'],
      fill: am5.color(0xeeeeee),
      stroke: am5.color(0xffffff),
    }),
  )
  polygonSeries.mapPolygons.template.setAll({
    tooltipText: undefined,
    interactive: false,
  })

  const heatSeries = chart.series.push(
    am5map.MapPointSeries.new(root, {
      latitudeField: 'latitude',
      longitudeField: 'longitude',
      valueField: 'value',
    }),
  )

  const circleTemplate = am5.Template.new<am5.Circle>({
    radius: 7,
    fillOpacity: 0.7,
    fill: am5.color(0xff0000),
    strokeWidth: 0,
    tooltipText: 'Value: {value}',
  })

  heatSeries.bullets.push(() => {
    return am5.Bullet.new(root, {
      sprite: am5.Circle.new(root, {}, circleTemplate),
    })
  })

  heatSeries.set('heatRules', [
    {
      target: circleTemplate,
      key: 'radius',
      dataField: 'value',
      min: 5,
      max: 20,
    },
    {
      target: circleTemplate,
      key: 'fill',
      dataField: 'value',
      min: am5.color(0x845ec2),
      max: am5.color(0xf9f871),
    },
  ])

  const heatLegend = chart.children.push(
    am5.HeatLegend.new(root, {
      orientation: 'vertical',
      startColor: am5.color(0x845ec2),
      endColor: am5.color(0xf9f871),
      startOpacity: 1,
      endOpacity: 1,
      startText: 'Low',
      endText: 'High',
      stepCount: 5,
      y: am5.percent(15),
      x: am5.percent(95),
      height: am5.percent(70),
    }),
  )

  const heatData = data.map((point) => ({
    latitude: point[0],
    longitude: point[1],
    value: point[2],
  }))

  heatSeries.data.setAll(heatData)

  if (heatData.length > 0) {
    const values = heatData.map((d) => d.value)
    const min = Math.min(...values)
    const max = Math.max(...values)
    heatLegend.setAll({
      startValue: min,
      endValue: max,
    })
  }
}

const initializeChart = () => {
  if (mapContainer.value) {
    if (am5Root) {
      am5Root.dispose()
    }
    am5Root = am5.Root.new(mapContainer.value)
    am5Root.setThemes([am5themes_Animated.new(am5Root)])

    if (props.chartType === 'map' && props.config.points) {
      renderBubbleMap(am5Root, props.config.points)
    } else if (props.chartType === 'heatmap' && props.config.data) {
      renderHeatmap(am5Root, props.config.data)
    }
  }
}

onMounted(() => {
  initializeChart()
})

watch(
  () => [props.config, props.chartType],
  () => {
    initializeChart()
  },
  { deep: true },
)

onBeforeUnmount(() => {
  if (am5Root) {
    am5Root.dispose()
    am5Root = null
  }
})
</script>

<template>
  <div ref="mapContainer" class="w-full h-full"></div>
</template>
